import { flames } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { formedBlade, gem, R, ramp, shaft, tassel } from './craft';

/**
 * Hand-made item pictures: long arms (spear, glaive, staves), the meteor hammer and the two
 * sceptres. Long pieces lie on the diagonal, butt bottom left.
 */

const oak = ramp('#a8743e');
const flame = ramp('#e8602a');

/** A metal band round a shaft at (x,y) on a 45° diagonal. */
function band(p: Pix, x: number, y: number, r = R.bronze): void {
  p.thick(x - 1, y - 1, x + 1, y + 1, r.b, 2);
  p.px(x + 1, y - 1, r.l2);
}

registerIcons('item', {
  // ทวนพู่แดง: a spear with a leaf head and a red horsehair tassel under it.
  'tassel-qiang': (p) => {
    shaft(p, 3, 29, 21, 11, R.wood);
    band(p, 4, 28, R.iron);
    // Tassel: a red cluster flaring back from the socket.
    p.poly(
      [
        [20, 12],
        [16, 11],
        [13, 14],
        [12, 19],
        [15, 17],
        [17, 18],
        [18, 14],
      ],
      R.red.b,
    );
    p.line(13, 15, 15, 12, R.red.l1);
    p.line(14, 18, 17, 15, R.red.d1);
    p.px(12, 19, R.red.d2).px(15, 17, R.red.d2);
    band(p, 21, 11, R.bronze);
    formedBlade(p, 22, 10, 30, 2, R.steel, { w: 4.4, taper: 0.35 });
  },
  // ง้าวจันทร์เสี้ยว: a pole glaive with a crescent blade and a back spike.
  'crescent-glaive': (p) => {
    shaft(p, 3, 29, 20, 12, R.lacquer);
    band(p, 20, 12, R.gold);
    band(p, 4, 28, R.gold);
    // The crescent: an arc of filled spans on the outside of the shaft.
    for (let k = 0; k < 4; k++) p.ring(21, 11, 8 - k, 8 - k, k < 2 ? R.steel.b : R.steel.d1, -2.4, -0.2);
    p.ring(21, 11, 9, 9, R.steel.l2, -2.2, -0.5);
    // The back spike and the tip.
    p.thick(21, 11, 25, 4, R.steel.d1, 2);
    p.px(26, 3, R.steel.l2);
    p.line(19, 13, 16, 10, R.steel.d1);
    p.px(15, 9, R.steel.l1);
    p.ell(21, 11, 1.6, 1.6, R.gold.b);
    p.px(21, 10, R.gold.l2);
  },
  // กระบองไม้โอ๊ก: a plain oak staff with iron caps and grain.
  'oak-staff': (p) => {
    shaft(p, 4, 28, 27, 5, oak, 3);
    for (const [x, y] of [
      [10, 21],
      [16, 15],
      [21, 10],
    ] as const)
      p.px(x, y, oak.d2);
    p.thick(3, 29, 6, 26, R.iron.d1, 3);
    p.thick(25, 7, 28, 4, R.iron.d1, 3);
    p.px(6, 25, R.iron.l1).px(28, 3, R.iron.l2);
    p.thick(14, 17, 17, 14, oak.d1, 3);
    p.px(16, 14, oak.l1);
  },
  // กระบองทลายภูผา (legend): a dark staff banded in gold, crowned with a jade peak.
  'u-mountain-staff': (p) => {
    shaft(p, 4, 29, 22, 11, R.ink, 3);
    for (const [x, y] of [
      [6, 27],
      [12, 21],
      [18, 15],
    ] as const) {
      p.thick(x - 1, y - 1, x + 1, y + 1, R.gold.d1, 3);
      p.px(x + 1, y - 2, R.gold.l2);
    }
    // The head: a three-step peak in jade on a gold socket.
    p.ell(23, 10, 3, 3, R.gold.b);
    p.poly(
      [
        [19, 9],
        [23, 1],
        [25, 4],
        [27, 2],
        [30, 9],
        [26, 12],
        [22, 12],
      ],
      R.jade.d1,
    );
    p.poly(
      [
        [23, 2],
        [26, 9],
        [22, 10],
      ],
      R.jade.b,
    );
    p.line(23, 2, 21, 7, R.jade.l2);
    p.line(27, 3, 29, 7, R.jade.l1);
    // Cracks from its weight.
    p.px(2, 26, R.gold.l1).px(1, 28, R.gold.l1);
  },
  // ลูกตุ้มดาวตก: a spiked iron ball on a chain wound round a short red cord.
  'meteor-hammer': (p) => {
    // Chain: alternating links from the handle loop to the ball.
    for (let i = 0; i < 7; i++) {
      const x = 7 + i * 2;
      const y = 25 - i * 2 + Math.round(Math.sin(i) * 1);
      p.px(x, y, i % 2 ? R.iron.b : R.iron.l1).px(x + 1, y, R.iron.d1);
    }
    p.ring(6, 26, 3, 3, R.red.b);
    p.px(5, 23, R.red.l1);
    p.thick(3, 29, 5, 27, R.red.d1, 2);
    // Ball and spikes.
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4 + 0.2;
      p.thick(22 + Math.cos(a) * 5, 11 + Math.sin(a) * 5, 22 + Math.cos(a) * 8, 11 + Math.sin(a) * 8, R.iron.d1, 1);
      p.px(22 + Math.cos(a) * 8, 11 + Math.sin(a) * 8, R.iron.l1);
    }
    p.ell(22, 11, 5.5, 5.5, R.iron.d1);
    p.ell(23, 10, 3.8, 3.8, R.iron.b);
    p.ell(24, 9, 1.6, 1.6, R.iron.l2);
  },
  // คทาลูกแก้วคราม: a bronze sceptre with an azure orb held in three prongs.
  'orb-sceptre': (p) => {
    shaft(p, 5, 29, 19, 15, R.bronze);
    band(p, 12, 22, R.gold);
    p.ell(5, 29, 1.6, 1.6, R.gold.b);
    // Prongs.
    p.path(
      [
        [19, 15],
        [17, 10],
        [18, 6],
      ],
      R.gold.d1,
    );
    p.path(
      [
        [19, 15],
        [24, 17],
        [27, 15],
      ],
      R.gold.d1,
    );
    gem(p, 22, 10, 5.4, R.azure);
    p.ring(22, 10, 5, 5, R.azure.d2, 1.3, 2.9);
    p.path(
      [
        [20, 15],
        [21, 12],
      ],
      R.gold.b,
    );
    p.px(18, 6, R.gold.l2).px(27, 15, R.gold.l2);
    tassel(p, 9, 24, 4, R.azure);
  },
  // คทาเปลวเพลิง: a red-lacquer sceptre with a gold cup of living flame.
  'flame-sceptre': (p) => {
    shaft(p, 4, 29, 18, 15, R.lacquer);
    band(p, 10, 23, R.gold);
    flames(p, 15, 13, 12, 11, flame.b, '#ffd060');
    p.rect(17, 8, 3, 3, flame.l2);
    // The cup.
    p.poly(
      [
        [14, 12],
        [28, 12],
        [24, 17],
        [18, 17],
      ],
      R.gold.d1,
    );
    p.line(15, 12, 27, 12, R.gold.l1);
    p.px(26, 12, R.gold.l2);
    gem(p, 21, 14, 1.5, R.red);
    p.px(24, 2, flame.l1).px(13, 5, flame.b).px(29, 6, C.fireLight);
  },
});
